import safeStorage from '@github-ui/safe-storage'
import {postTask} from '@github-ui/scheduler'
import {ssrSafeWindow} from '@github-ui/ssr-utils'

import type {ClearKeysOptions, WriteOptions} from './types'

export type Listener = () => void

// Global registry of all store caches for test cleanup
// Exposed on window so tests can clear without importing (avoids vi.mock issues)
const allCaches: Array<Map<string, unknown>> = []

declare global {
  interface Window {
    __SAFE_STORAGE_CLEAR_ALL_CACHES__?: () => void
    __SAFE_STORAGE_FORCE_SYNC_WRITES__?: boolean
  }
}

// Chain onto any previously-registered cleanup (e.g. the cookie store) so all
// caches clear together regardless of module load order.
if (typeof window !== 'undefined') {
  const previousClear = window.__SAFE_STORAGE_CLEAR_ALL_CACHES__
  window.__SAFE_STORAGE_CLEAR_ALL_CACHES__ = () => {
    previousClear?.()
    for (const cache of allCaches) {
      cache.clear()
    }
  }
}

export const createStore = (storageType: 'localStorage' | 'sessionStorage') => {
  const storage = safeStorage(storageType)

  const cache = new Map<string, unknown>()
  const listeners = new Map<string, Set<Listener>>()

  // Register this cache for global cleanup
  allCaches.push(cache)

  // Deferred storage persistence: batch writes and flush via postTask
  // so JSON.stringify + setItem don't block the interaction handler
  const pendingWrites = new Map<string, unknown>()
  let flushScheduled = false

  function flushToStorage(): void {
    try {
      for (const [k, v] of pendingWrites) {
        try {
          storage.setItem(k, JSON.stringify(v))
        } catch {
          // Skip keys that fail to serialize (e.g. circular refs)
        }
      }
      pendingWrites.clear()
    } finally {
      flushScheduled = false
    }
  }

  function scheduleFlush(): void {
    if (!flushScheduled) {
      flushScheduled = true
      postTask(flushToStorage, {priority: 'background'})
    }
  }

  // Flush pending writes when the page is backgrounded/closed so deferred
  // data isn't lost on tab close or navigation.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushToStorage()
      }
    })
  }

  function isEqual<T>(a: T, b: T): boolean {
    if (a === b) return true
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch {
      // Circular references or other non-serializable values
      return false
    }
  }

  function emit(key: string): void {
    const set = listeners.get(key)
    if (set) {
      for (const listener of set) {
        listener()
      }
    }
  }

  function subscribe(key: string, listener: Listener): () => void {
    let set = listeners.get(key)
    if (!set) {
      set = new Set()
      listeners.set(key, set)
    }
    set.add(listener)
    return () => {
      set.delete(listener)
      // Clean up empty listener sets to prevent memory leaks
      if (set.size === 0) {
        listeners.delete(key)
      }
    }
  }

  function readStorage<T>(key: string, initial: T): T {
    // Always return cached value if present - critical for useSyncExternalStore contract
    if (cache.has(key)) {
      return cache.get(key) as T
    }

    try {
      const raw = storage.getItem(key)
      const value = raw == null ? initial : JSON.parse(raw)
      cache.set(key, value)
      return value
    } catch {
      cache.set(key, initial)
      return initial
    }
  }

  function writeStorage<T>(key: string, value: T, options?: WriteOptions): void {
    const prev = cache.get(key)
    if (isEqual(prev, value)) return

    // Update cache and notify subscribers synchronously
    cache.set(key, value)
    emit(key)

    if (options?.flush || ssrSafeWindow?.__SAFE_STORAGE_FORCE_SYNC_WRITES__) {
      // Write immediately — used for critical data that must survive tab close,
      // or forced sync in test environments
      pendingWrites.delete(key)
      storage.setItem(key, JSON.stringify(value))
    } else {
      // Defer persistence to avoid JSON.stringify + setItem blocking the handler
      pendingWrites.set(key, value)
      scheduleFlush()
    }
  }

  /**
   * Remove a key from storage and cache, notifying subscribers
   */
  function removeStorage(key: string): void {
    pendingWrites.delete(key)
    cache.delete(key)
    storage.removeItem(key)
    emit(key)
  }

  /**
   * Reset a key to a default value atomically - updates cache before emitting
   * to avoid timing issues with useSyncExternalStore snapshots
   */
  function resetStorage<T>(key: string, defaultValue: T): void {
    const prev = cache.get(key)
    if (isEqual(prev, defaultValue)) return

    pendingWrites.delete(key)
    cache.set(key, defaultValue) // Set default BEFORE emit
    storage.removeItem(key)
    emit(key)
  }

  /**
   * Clear all cached data (useful for testing).
   * Emits to subscribers - components should handle cache miss in getSnapshot
   * by reseeding with their initialValue.
   */
  function clearAll(): void {
    pendingWrites.clear()
    const keys = [...cache.keys()]
    for (const key of keys) {
      cache.delete(key)
      storage.removeItem(key)
      emit(key)
    }
  }

  /**
   * Clear specified keys from storage and cache.
   * By default, notifies subscribers so components re-render.
   * When `silent` is true, skips notification — existing mounted components
   * keep their current snapshot, but new reads will see the key is gone.
   */
  function clearKeys(keys: string[], options?: ClearKeysOptions): void {
    for (const key of keys) {
      pendingWrites.delete(key)
      cache.delete(key)
      storage.removeItem(key)
      if (!options?.silent) {
        emit(key)
      }
    }
  }

  /**
   * Clear keys matching a prefix from cache and storage.
   * Emits to subscribers - components should handle cache miss in getSnapshot
   * by reseeding with their initialValue.
   */
  function clearKeysByPrefix(prefix: string): void {
    const keysToRemove = getKeysByPrefix(prefix)

    for (const key of keysToRemove) {
      pendingWrites.delete(key)
      cache.delete(key)
      storage.removeItem(key)
      emit(key)
    }
  }

  /**
   * Get all items whose keys start with a prefix
   */
  function getItemsByPrefix<T>(prefix: string): T[] {
    const items: T[] = []
    const keys = getKeysByPrefix(prefix)

    for (const key of keys) {
      if (cache.has(key)) {
        items.push(cache.get(key) as T)
      } else {
        const raw = storage.getItem(key)
        if (raw != null) {
          try {
            items.push(JSON.parse(raw) as T)
          } catch {
            // Skip invalid JSON
          }
        }
      }
    }

    return items
  }

  /**
   * Get all keys from cache and storage that start with the provided prefix, de-duplicated.
   */
  function getKeysByPrefix(prefix: string): string[] {
    const cacheKeys = [...cache.keys()].filter(key => key.startsWith(prefix))
    const storageKeys = storage.getKeys().filter(key => key.startsWith(prefix))
    return [...new Set([...cacheKeys, ...storageKeys])]
  }

  return {
    writeStorage,
    readStorage,
    removeStorage,
    resetStorage,
    subscribe,
    cache,
    clearAll,
    clearKeys,
    clearKeysByPrefix,
    getItemsByPrefix,
    getKeysByPrefix,
  }
}
